import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import i18n from 'i18next';
import detector from 'i18next-browser-languagedetector';
import backend from 'i18next-http-backend';

import store from './state/store';
import { initSession, checkSession } from './loaders/profile';
import App from './App';
import theme from './theme';
import { register } from './api/qos';

import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { Logger, LogLevel, ApiError, AuthError, NetworkError } from '@bimdata/client';
import moment from 'moment';

window.onerror = function (message: string, file, line, column, error) {
  if (
    error &&
    !(error instanceof AuthError) &&
    !((error instanceof ApiError) && (error.code === 404 || error.code === 403 || error.code === 401)) &&
    !(error instanceof NetworkError)
  ) {
    register({
      time: (new Date()).toISOString(),
      message,
      //@ts-ignore
      ...(error?.code ? { code: error.code } : {}),
      file,
      line,
      column,
      ...(error ? { stack: error.stack } : {})
    }).catch((err) => {
      Logger.error(err);
    });
  }
  return true;
};

window.onunhandledrejection = (e: PromiseRejectionEvent) => {
  throw e.reason;
}

document.addEventListener("visibilitychange", () => {
  if (!document.hidden) {
    checkSession();
  }
});

const root = document.getElementById('root');
if (root) {
  i18n
    .use(backend)
    .use(detector)
    .init({
      debug: import.meta.env.MODE === 'development',
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
      },
      fallbackLng: "ru-RU",
      ns: ['common'],
      defaultNS: 'common',
      interpolation: { escapeValue: false }
    }).then(() => {
      moment.defineLocale('date-card-locale', {
        parentLocale: 'en',
        monthsShort: [
          i18n.t('date.month.short.january'),
          i18n.t('date.month.short.february'),
          i18n.t('date.month.short.march'),
          i18n.t('date.month.short.april'),
          i18n.t('date.month.short.may'),
          i18n.t('date.month.short.june'),
          i18n.t('date.month.short.july'),
          i18n.t('date.month.short.august'),
          i18n.t('date.month.short.september'),
          i18n.t('date.month.short.october'),
          i18n.t('date.month.short.november'),
          i18n.t('date.month.short.december'),
        ]
      });

      i18n.services.formatter!.add('deletable', (value, lng, options) => {
        const deleted = options && (options.deleted === true || options.deleted === 'true');
        return deleted ? `<del>${value}</del>` : value;
      });

      Logger.level = LogLevel[import.meta.env.VITE_LOG_LEVEL || 'NONE'];
      if (import.meta.env.VITE_YM_COUNTER_NUMBER) {
        //@ts-ignore
        window.ym(
          import.meta.env.VITE_YM_COUNTER_NUMBER,
          "init",
          {
            defer: true,
            clickmap:true,
            trackLinks:true,
            accurateTrackBounce:true,
            webvisor:true
          }
        );

        //@ts-ignore
        window.ym(
          import.meta.env.VITE_YM_COUNTER_NUMBER,
          'hit',
          window.location.href,
          {
            title: document.title,
            referer: document.referrer
          }
        );
      }
  
      initSession().finally(() => {
        ReactDOM.createRoot(root).render(
          <Provider store={store}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <App />
            </ThemeProvider>
          </Provider>
        );
      });
    }).catch((err) => {
      console.error(err);
    });
}